import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Target,
  Brain,
  Shield,
  Zap,
  Globe,
  Building2,
  Users,
  GraduationCap,
  Award,
  CheckCircle,
  Sparkles,
  BarChart3,
  FlaskConical,
  Route,
  Eye,
  BookOpen,
  Briefcase,
  UserCheck,
  Scale,
  Network,
  Gauge,
  MapPin,
  Mail,
  Linkedin
} from 'lucide-react';

// Cerebro Logo URL
// Cerebro Logo - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-cerebro-primary">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-cerebro-primary/95 backdrop-blur-xl border-b border-cerebro-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={CEREBRO_LOGO} alt="Cerebro" className="h-10 w-auto" />
              <span className="text-white font-bold text-lg hidden sm:block">Cerebro</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/courses" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">
                Courses
              </Link>
              <Link to="/about" className="text-cerebro-accent text-sm font-medium">
                About
              </Link>
              <Link to="/#pricing" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">
                Pricing
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login" className="text-slate-300 hover:text-white text-sm font-medium px-4 py-2">
                Sign In
              </Link>
              <Link to="/register" className="bg-cerebro-accent hover:bg-cerebro-accent/90 text-cerebro-primary text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-cerebro-primary via-cerebro-secondary/10 to-cerebro-primary" />
          <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-cerebro-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Logo with Teal Halo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-cerebro-accent/15 blur-[60px] rounded-full scale-[1.8]" />
              <div className="absolute inset-0 bg-cerebro-accent/25 blur-[40px] rounded-full scale-[1.3]" />
              <img src={CEREBRO_LOGO} alt="Cerebro" className="relative h-40 w-auto" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            About <span className="text-cerebro-accent">Cerebro</span>
          </h1>
          <p className="text-2xl text-cerebro-gold font-medium mb-6">
            Where Learning Meets Intelligence
          </p>
          <p className="text-lg text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Cerebro is the enterprise learning and capability development platform of the Navigate Compliance Group — 
            built to equip organisations and professionals with the skills required to lead in governance, risk, 
            compliance, cybersecurity, AI governance, and digital trust.
          </p>
        </div>
      </section>

      {/* Core Statement */}
      <section className="py-16 bg-navigate-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-cerebro-primary/50 border border-cerebro-accent/30 rounded-2xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-cerebro-accent/20 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-8 w-8 text-cerebro-accent" />
            </div>
            <p className="text-xl md:text-2xl text-white font-medium leading-relaxed mb-6">
              We exist at the intersection of <span className="text-cerebro-accent">learning</span>, <span className="text-cerebro-gold">regulation</span>, and <span className="text-cerebro-accent">technology</span>.
            </p>
            <p className="text-lg text-slate-400">
              Cerebro is not a course catalogue. It is a structured capability ecosystem designed for modern regulatory and digital environments.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-24 bg-cerebro-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-accent/10 border border-cerebro-accent/30 mb-6">
                <Target className="h-4 w-4 text-cerebro-accent" />
                <span className="text-sm text-cerebro-accent font-medium">OUR MISSION</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Strengthening Governance & Digital Trust
              </h2>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                Our mission is to strengthen governance and digital trust by building future-ready professionals and resilient organisations.
              </p>
              <p className="text-slate-300 font-medium mb-6">We believe that:</p>
            </div>

            <div className="space-y-4">
              {[
                { icon: Scale, text: 'Compliance must evolve beyond theory.' },
                { icon: Brain, text: 'Risk leaders must understand technology.' },
                { icon: Shield, text: 'AI governance requires structured oversight.' },
                { icon: BarChart3, text: 'Continuous learning must be practical and measurable.' },
                { icon: Globe, text: 'Emerging markets deserve world-class professional capability infrastructure.' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-navigate-surface rounded-xl border border-cerebro-secondary/30">
                  <div className="w-10 h-10 rounded-lg bg-cerebro-accent/10 flex items-center justify-center shrink-0">
                    <item.icon className="h-5 w-5 text-cerebro-accent" />
                  </div>
                  <p className="text-slate-300 pt-2">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-xl text-cerebro-gold font-medium">
              Cerebro was built to deliver exactly that.
            </p>
          </div>
        </div>
      </section>

      {/* Navigate Compliance Group */}
      <section className="py-24 bg-navigate-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-gold/10 border border-cerebro-gold/30 mb-6">
              <Building2 className="h-4 w-4 text-cerebro-gold" />
              <span className="text-sm text-cerebro-gold font-medium">PART OF NAVIGATE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Part of the Navigate Compliance Group
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Cerebro forms part of the Navigate Compliance Group — a recognised governance and regulatory advisory practice 
              operating across financial services, fintech, corporate, and public sector environments.
            </p>
          </div>

          <div className="bg-cerebro-primary/50 border border-cerebro-secondary/30 rounded-2xl p-8">
            <p className="text-slate-300 mb-6 text-center">
              Through Navigate's deep advisory and operational experience, Cerebro's learning pathways are aligned to:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Scale, text: 'Real regulatory requirements' },
                { icon: Building2, text: 'Enterprise governance frameworks' },
                { icon: Users, text: 'Board and regulator expectations' },
                { icon: Zap, text: 'Emerging digital and AI risks' }
              ].map((item, idx) => (
                <div key={idx} className="text-center p-4">
                  <div className="w-12 h-12 rounded-xl bg-cerebro-gold/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-6 w-6 text-cerebro-gold" />
                  </div>
                  <p className="text-white font-medium">{item.text}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-center mt-8 italic">
              This ensures our learning is practical, applied, and aligned to real-world professional demands.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24 bg-cerebro-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-accent/10 border border-cerebro-accent/30 mb-6">
              <Users className="h-4 w-4 text-cerebro-accent" />
              <span className="text-sm text-cerebro-accent font-medium">WHO WE SERVE</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Cerebro is designed for
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Enterprise Organisations */}
            <div className="bg-navigate-surface border border-cerebro-secondary/30 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-cerebro-secondary/30 flex items-center justify-center">
                  <Building2 className="h-7 w-7 text-cerebro-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Enterprise Organisations</h3>
                  <p className="text-sm text-slate-500">Scalable learning ecosystems</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  'Risk & Compliance teams',
                  'IT Governance & Cybersecurity functions',
                  'AI & Digital Transformation leaders',
                  'HR and Learning & Development departments',
                  'Boards and Executive Committees'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-cerebro-accent shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-400 border-t border-cerebro-secondary/30 pt-6">
                We provide scalable learning ecosystems, simulation-based capability development, and structured pathway recognition.
              </p>
            </div>

            {/* Individual Professionals */}
            <div className="bg-navigate-surface border border-cerebro-secondary/30 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-cerebro-gold/20 flex items-center justify-center">
                  <UserCheck className="h-7 w-7 text-cerebro-gold" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Individual Professionals</h3>
                  <p className="text-sm text-slate-500">Continuous professional development</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  'Compliance Officers',
                  'Risk Managers',
                  'Cybersecurity Specialists',
                  'IT Governance Practitioners',
                  'AI Governance Leaders',
                  'Emerging professionals building career momentum'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="h-5 w-5 text-cerebro-gold shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-slate-400 border-t border-cerebro-secondary/30 pt-6">
                Our platform supports continuous professional development through structured pathways, simulations, and capability recognition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-navigate-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-accent/10 border border-cerebro-accent/30 mb-6">
              <Route className="h-4 w-4 text-cerebro-accent" />
              <span className="text-sm text-cerebro-accent font-medium">OUR APPROACH</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Learning Designed to Prepare
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Learning is designed not just to inform — but to prepare.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Cerebro Integrates */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">Cerebro integrates:</h3>
              <div className="space-y-4">
                {[
                  { icon: BookOpen, text: 'Micro-learning and structured pathways' },
                  { icon: FlaskConical, text: 'Crisis and regulatory simulations' },
                  { icon: Brain, text: 'AI-powered learning recommendations' },
                  { icon: Gauge, text: 'Capability maturity diagnostics' },
                  { icon: BarChart3, text: 'Enterprise analytics and reporting' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-cerebro-primary/50 rounded-xl border border-cerebro-secondary/30">
                    <div className="w-10 h-10 rounded-lg bg-cerebro-accent/10 flex items-center justify-center shrink-0">
                      <item.icon className="h-5 w-5 text-cerebro-accent" />
                    </div>
                    <p className="text-slate-300">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* We Focus On */}
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">We focus on:</h3>
              <div className="space-y-4">
                {[
                  'Regulatory alignment',
                  'Applied scenarios',
                  'Real-world judgement',
                  'Executive communication readiness',
                  'Governance architecture thinking'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-cerebro-primary/50 rounded-xl border border-cerebro-gold/20">
                    <div className="w-10 h-10 rounded-lg bg-cerebro-gold/10 flex items-center justify-center shrink-0">
                      <Target className="h-5 w-5 text-cerebro-gold" />
                    </div>
                    <p className="text-slate-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Bodies & SETAs */}
      <section className="py-24 bg-cerebro-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-gold/10 border border-cerebro-gold/30 mb-6">
                <Award className="h-4 w-4 text-cerebro-gold" />
                <span className="text-sm text-cerebro-gold font-medium">PARTNERSHIPS</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Working With Professional Bodies & SETAs
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed">
                Cerebro works closely with professional bodies and Sector Education and Training Authorities (SETAs) to support 
                the broader professional ecosystem — ensuring that practitioners remain relevant, competent, and aligned to 
                evolving regulatory and technological landscapes.
              </p>
            </div>

            <div className="space-y-4">
              {[
                'Align learning content to recognised professional standards',
                'Support structured CPD and continuous development requirements',
                'Contribute to professional capability frameworks',
                'Strengthen industry-wide governance and digital literacy'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-navigate-surface rounded-xl border border-cerebro-secondary/30">
                  <CheckCircle className="h-6 w-6 text-cerebro-gold shrink-0 mt-0.5" />
                  <p className="text-slate-300">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Emerging Markets Commitment */}
      <section className="py-24 bg-navigate-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-accent/10 border border-cerebro-accent/30 mb-6">
              <Globe className="h-4 w-4 text-cerebro-accent" />
              <span className="text-sm text-cerebro-accent font-medium">EMERGING MARKETS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Our Commitment to Emerging Markets
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="bg-cerebro-primary/50 border border-cerebro-secondary/30 rounded-2xl p-8">
              <p className="text-slate-300 mb-6">We recognise that emerging markets face:</p>
              <div className="space-y-3">
                {[
                  'Rapid regulatory change',
                  'Infrastructure constraints',
                  'Increasing digital transformation',
                  'Growing AI adoption',
                  'Heightened regulatory scrutiny'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-cerebro-accent" />
                    <p className="text-slate-400">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center lg:text-left">
              <p className="text-xl text-white leading-relaxed">
                Cerebro is built to support professionals operating in these dynamic environments — equipping them with{' '}
                <span className="text-cerebro-accent font-medium">globally informed</span>, <span className="text-cerebro-gold font-medium">locally relevant</span> capability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Cerebro Different */}
      <section className="py-24 bg-cerebro-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-gold/10 border border-cerebro-gold/30 mb-6">
              <Sparkles className="h-4 w-4 text-cerebro-gold" />
              <span className="text-sm text-cerebro-gold font-medium">DIFFERENTIATORS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              What Makes Cerebro Different
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Target, text: 'Designed specifically for GRC, IT, Cyber & AI Governance professionals' },
              { icon: Briefcase, text: 'Built by practitioners, not content aggregators' },
              { icon: Network, text: 'Integrated with enterprise governance architecture' },
              { icon: Gauge, text: 'Focused on measurable capability, not course completion' },
              { icon: Route, text: 'Structured pathways with meaningful recognition' },
              { icon: Award, text: 'Professional certification and badge system' }
            ].map((item, idx) => (
              <div key={idx} className="bg-navigate-surface border border-cerebro-secondary/30 rounded-xl p-6 hover:border-cerebro-gold/50 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-cerebro-gold/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-cerebro-gold" />
                </div>
                <p className="text-slate-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-24 bg-gradient-to-b from-navigate-surface to-cerebro-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cerebro-accent/10 border border-cerebro-accent/30 mb-6">
            <Eye className="h-4 w-4 text-cerebro-accent" />
            <span className="text-sm text-cerebro-accent font-medium">OUR VISION</span>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-cerebro-accent/10 blur-3xl rounded-full scale-150" />
            <div className="relative bg-cerebro-primary/80 border border-cerebro-accent/30 rounded-2xl p-8 md:p-12">
              <p className="text-2xl md:text-3xl text-white font-medium leading-relaxed">
                To become <span className="text-cerebro-accent">Africa's</span> and <span className="text-cerebro-gold">emerging markets'</span> leading governance intelligence learning ecosystem — enabling organisations and professionals to operate confidently in complex regulatory and digital environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-cerebro-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={CEREBRO_LOGO} alt="Cerebro" className="h-20 w-auto mx-auto mb-8" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Build Future-Ready Capability?
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Join the organisations and professionals who trust Cerebro for their governance and compliance learning needs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cerebro-accent text-cerebro-primary font-semibold rounded-xl hover:bg-cerebro-accent/90 transition-all shadow-lg shadow-cerebro-accent/25"
            >
              Get Started
              <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="mailto:hello@navigatelearning.io?subject=Enterprise Demo Request"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cerebro-secondary text-white font-semibold rounded-xl border border-cerebro-secondary hover:bg-cerebro-secondary/80 transition-all"
            >
              Request a Demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-cerebro-primary border-t border-cerebro-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <img src={CEREBRO_LOGO} alt="Cerebro" className="h-16 w-auto mb-4" />
              <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
                Enterprise Learning & Continuous Professional Development. 
                Where learning meets intelligence.
              </p>
              <p className="text-sm text-slate-500">
                Cerebro is part of the Navigate Group (Pty) Ltd · Registered in South Africa
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Platform</h4>
              <ul className="space-y-3">
                {[
                  { label: 'Courses', href: '/courses' },
                  { label: 'Learning Labs', href: '/courses' },
                  { label: 'Learning Pathways', href: '/courses' },
                  { label: 'About Us', href: '/about' }
                ].map((link, idx) => (
                  <li key={idx}>
                    <Link to={link.href} className="text-slate-400 hover:text-cerebro-accent transition-colors text-sm">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-slate-400 text-sm">
                  <MapPin className="h-4 w-4 text-cerebro-accent" />
                  Johannesburg, South Africa
                </li>
                <li>
                  <a href="mailto:hello@navigatelearning.io" className="flex items-center gap-2 text-slate-400 hover:text-cerebro-accent transition-colors text-sm">
                    <Mail className="h-4 w-4" />
                    hello@navigatelearning.io
                  </a>
                </li>
                <li>
                  <a href="https://www.navigatecompliance.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-cerebro-accent transition-colors text-sm">
                    <Globe className="h-4 w-4" />
                    www.navigatecompliance.io
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-cerebro-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <a href="https://www.linkedin.com/company/navigate-compliance/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cerebro-accent transition-colors flex items-center gap-1 text-sm">
                  <Linkedin className="h-4 w-4" />
                  LinkedIn
                </a>
                <span className="text-cerebro-secondary/50">|</span>
                <Link to="/responsible-ai" className="text-cerebro-accent hover:text-cerebro-accent/80 text-sm">
                  Responsible AI
                </Link>
                <span className="text-cerebro-secondary/50">|</span>
                <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Privacy
                </Link>
                <span className="text-cerebro-secondary/50">|</span>
                <Link to="/terms" className="text-slate-400 hover:text-white transition-colors text-sm">
                  Terms of Use
                </Link>
              </div>
              <p className="text-sm text-slate-600">
                © {new Date().getFullYear()} Navigate Compliance Group. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
